import type {
  EndlessWaveGenerationConfig,
  EndlessWaveCandidate,
  WaveConfig,
  WaveGroup,
} from "@bastion/data";
import { SeededRandom } from "../clock/SeededRandom";

const MAX_SEARCH_NODES = 2_048;
const MIN_WEIGHT = 0.01;

type Candidate = EndlessWaveCandidate;
type Domains = Array<Set<string>>;

/**
 * 为一个无尽波次生成标准 WaveConfig。
 * 每波随机流由 Seed、地图和波号派生，不会消耗战斗系统的随机序列。
 */
export function generateEndlessWave(
  config: EndlessWaveGenerationConfig,
  enemies: Record<string, { isBoss?: boolean }>,
  bosses: Record<string, { isBoss: true }>,
  seed: number,
  mapId: string,
  waveNumber: number,
): WaveConfig {
  if (!Number.isInteger(waveNumber) || waveNumber < config.startWave) {
    throw new Error(`Invalid endless wave number: ${waveNumber}`);
  }

  const waveSeed = deriveSeed(seed, mapId, waveNumber, "endless-wave");
  const random = new SeededRandom(waveSeed);
  const candidates = [...config.ordinaryRoster, ...config.bossRoster];
  const noiseSeed = deriveSeed(seed, mapId, 0, "endless-perlin");
  const scale = interpolate(
    config.perlin.scale.min,
    config.perlin.scale.max,
    new SeededRandom(deriveSeed(seed, mapId, 0, "endless-scale")).next(),
  );
  const phase = new SeededRandom(deriveSeed(seed, mapId, 0, "endless-phase")).next() * 256;
  const noise = samplePerlinNoise((waveNumber - config.startWave) / scale + phase, noiseSeed);
  const normalizedNoise = clamp((noise + 1) / 2, 0, 1);
  const pressure = interpolate(
    config.perlin.pressure.min,
    config.perlin.pressure.max,
    normalizedNoise,
  );
  const slotCount = Math.max(
    1,
    Math.round(
      interpolate(
        config.generation.groupSlots.min,
        config.generation.groupSlots.max,
        normalizedNoise,
      ),
    ),
  );
  const effectiveWeights = new Map(
    candidates.map((candidate) => [
      candidate.enemyId,
      candidateWeight(candidate, waveNumber, scale, phase, noiseSeed),
    ]),
  );

  const collapsed = collapseWave(slotCount, candidates, config, effectiveWeights, random);
  const groups = collapsed.map((enemyId, index) => {
    const isBoss = config.bossRoster.some((candidate) => candidate.enemyId === enemyId);
    const count = isBoss
      ? 1
      : Math.max(
          config.generation.groupCount.min,
          Math.round(
            interpolate(
              config.generation.groupCount.min,
              config.generation.groupCount.max,
              clamp(
                (pressure - config.perlin.pressure.min) /
                  (config.perlin.pressure.max - config.perlin.pressure.min || 1),
                0,
                1,
              ),
            ),
          ),
        );
    const interval = interpolate(
      config.generation.spawnInterval.min,
      config.generation.spawnInterval.max,
      random.next(),
    );
    const delay =
      index === 0
        ? config.generation.delay.min
        : interpolate(config.generation.delay.min, config.generation.delay.max, random.next());
    return { enemyId, count, spawnInterval: interval, delay } satisfies WaveGroup;
  });

  const reward = config.rewardSchedule.find(
    (entry) =>
      waveNumber >= entry.waveFrom && (entry.waveTo === 9999 || waveNumber <= entry.waveTo),
  );
  if (!reward) throw new Error(`No endless reward configured for wave ${waveNumber}`);

  // 配置经 data 校验保证引用正确；运行时仍保留防御性检查，避免坏内容生成幽灵单位。
  for (const candidate of candidates) {
    if (!enemies[candidate.enemyId] && !bosses[candidate.enemyId]) {
      throw new Error(`Unknown endless enemy "${candidate.enemyId}"`);
    }
  }

  return {
    id: `endless-wave-${waveNumber}`,
    wave: waveNumber,
    mapId,
    groups,
    rewardGold: reward.rewardGold,
  };
}

function collapseWave(
  slotCount: number,
  candidates: Candidate[],
  config: EndlessWaveGenerationConfig,
  weights: Map<string, number>,
  random: SeededRandom,
): string[] {
  const ids = candidates.map((candidate) => candidate.enemyId);
  const initial: Domains = Array.from({ length: slotCount }, () => new Set(ids));
  let searchedNodes = 0;

  const solve = (domains: Domains): Domains | null => {
    if (++searchedNodes > MAX_SEARCH_NODES) return null;
    const slot = selectLowestEntropySlot(domains, weights, random);
    if (slot < 0) return domains;

    for (const candidate of weightedOrder(domains[slot], weights, random)) {
      const collapsed = domains.map((domain) => new Set(domain));
      collapsed[slot] = new Set([candidate]);
      if (
        propagate(
          collapsed,
          config.adjacency,
          config.bossRoster,
          config.generation.maxBossesPerWave,
        ) &&
        countForcedBosses(collapsed, config.bossRoster) <= config.generation.maxBossesPerWave
      ) {
        const solved = solve(collapsed);
        if (solved) return solved;
      }
    }
    return null;
  };

  const solved = solve(initial);
  if (solved) return solved.map((domain) => domain.values().next().value as string);

  // 经校验的自兼容普通候选可重复填充任意长度，作为有限搜索的确定性兜底。
  const fallback = candidates.find(
    (candidate) =>
      !config.bossRoster.some((boss) => boss.enemyId === candidate.enemyId) &&
      config.adjacency[candidate.enemyId]?.includes(candidate.enemyId),
  );
  if (!fallback) {
    throw new Error("Endless WFC has no valid self-compatible fallback candidate");
  }
  return Array.from({ length: slotCount }, () => fallback.enemyId);
}

function hasLeftSupport(
  left: Set<string>,
  candidate: string,
  adjacency: Record<string, string[]>,
): boolean {
  for (const previous of left) {
    if ((adjacency[previous] ?? []).includes(candidate)) return true;
  }
  return false;
}

function countForcedBosses(domains: Domains, bossRoster: Candidate[]): number {
  return domains.filter(
    (domain) => domain.size === 1 && bossRoster.some((boss) => domain.has(boss.enemyId)),
  ).length;
}

function propagate(
  domains: Domains,
  adjacency: Record<string, string[]>,
  bossRoster: Candidate[],
  maxBosses: number,
): boolean {
  let changed = true;
  while (changed) {
    changed = false;
    const forcedBosses = domains.filter(
      (domain) => domain.size === 1 && bossRoster.some((boss) => domain.has(boss.enemyId)),
    ).length;
    if (forcedBosses > maxBosses) return false;

    if (forcedBosses === maxBosses) {
      for (const domain of domains) {
        if (domain.size <= 1) continue;
        for (const boss of bossRoster) {
          if (domain.delete(boss.enemyId)) changed = true;
        }
        if (domain.size === 0) return false;
      }
    }

    for (let slot = 0; slot < domains.length - 1; slot += 1) {
      const left = domains[slot];
      const right = domains[slot + 1];
      for (const candidate of left) {
        if (!(adjacency[candidate] ?? []).some((next) => right.has(next))) {
          left.delete(candidate);
          changed = true;
        }
      }
      if (left.size === 0) return false;
      for (const candidate of right) {
        if (!hasLeftSupport(left, candidate, adjacency)) {
          right.delete(candidate);
          changed = true;
        }
      }
      if (right.size === 0) return false;
    }
  }
  return true;
}

function selectLowestEntropySlot(
  domains: Domains,
  weights: Map<string, number>,
  random: SeededRandom,
): number {
  let selected = -1;
  let lowestEntropy = Number.POSITIVE_INFINITY;
  for (let index = 0; index < domains.length; index += 1) {
    const domain = domains[index];
    if (domain.size <= 1) continue;
    const values = [...domain];
    const totalWeight = values.reduce((sum, id) => sum + (weights.get(id) ?? MIN_WEIGHT), 0);
    const entropy =
      Math.log(totalWeight) -
      values.reduce((sum, id) => {
        const weight = weights.get(id) ?? MIN_WEIGHT;
        return sum + (weight * Math.log(weight)) / totalWeight;
      }, 0);
    const jitter = random.next() * 1e-9;
    if (entropy + jitter < lowestEntropy) {
      lowestEntropy = entropy + jitter;
      selected = index;
    }
  }
  return selected;
}

function weightedOrder(
  domain: Set<string>,
  weights: Map<string, number>,
  random: SeededRandom,
): string[] {
  return [...domain]
    .map((enemyId) => {
      const weight = Math.max(MIN_WEIGHT, weights.get(enemyId) ?? MIN_WEIGHT);
      return { enemyId, key: -Math.log(Math.max(Number.MIN_VALUE, random.next())) / weight };
    })
    .sort((left, right) => left.key - right.key)
    .map(({ enemyId }) => enemyId);
}

function candidateWeight(
  candidate: Candidate,
  waveNumber: number,
  scale: number,
  phase: number,
  seed: number,
): number {
  const candidateOffset = hashString(candidate.enemyId) % 10_000;
  const modulation = samplePerlinNoise(waveNumber / scale + phase + candidateOffset, seed);
  return Math.max(MIN_WEIGHT, candidate.baseWeight * (1 + modulation * candidate.noiseAffinity));
}

/** 一维 Perlin 梯度噪声：格点梯度、四次平滑插值，返回 [-1, 1]。 */
export function samplePerlinNoise(x: number, seed: number): number {
  const left = Math.floor(x);
  const fraction = x - left;
  const leftGradient = latticeGradient(left, seed);
  const rightGradient = latticeGradient(left + 1, seed);
  const leftValue = leftGradient * fraction;
  const rightValue = rightGradient * (fraction - 1);
  const smooth = fraction * fraction * fraction * (fraction * (fraction * 6 - 15) + 10);
  return clamp((leftValue + (rightValue - leftValue) * smooth) * 2, -1, 1);
}

function latticeGradient(lattice: number, seed: number): number {
  return (mix32((seed ^ Math.imul(lattice, 0x9e3779b1)) >>> 0) & 1) === 0 ? -1 : 1;
}

function deriveSeed(seed: number, mapId: string, waveNumber: number, label: string): number {
  return mix32(
    (seed ^ hashString(mapId) ^ Math.imul(waveNumber, 0x85ebca6b) ^ hashString(label)) >>> 0,
  );
}

function hashString(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function mix32(value: number): number {
  let result = value;
  result = Math.imul(result ^ (result >>> 16), 0x7feb352d);
  result = Math.imul(result ^ (result >>> 15), 0x846ca68b);
  return (result ^ (result >>> 16)) >>> 0;
}

function interpolate(min: number, max: number, amount: number): number {
  return min + (max - min) * clamp(amount, 0, 1);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
